import React, { useState, useCallback } from "react";
import {
  TextField,
  Typography,
  Button,
  Fade,
  Slide,
  Input,
  Link,
} from "@mui/material";
import withStyles from '@mui/styles/withStyles';
import axios from "axios";
import { useHistory } from "react-router-dom";

const styles = (theme) => ({
  "@keyframes slideInUp": {
    "0%": {
      transform: "translateY(30px)",
      opacity: 0,
    },
    "100%": {
      transform: "translateY(0)",
      opacity: 1,
    },
  },

  "@keyframes fadeInScale": {
    "0%": {
      transform: "scale(0.9)",
      opacity: 0,
    },
    "100%": {
      transform: "scale(1)",
      opacity: 1,
    },
  },

  wrapper: {
    minHeight: "100vh",
    background: theme.palette.mode === "dark"
      ? "linear-gradient(135deg, #0f0f23 0%, #1a1a2e 100%)"
      : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(2),
    position: "relative",
    overflow: "hidden",

    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `radial-gradient(circle at 20% 80%, ${theme.palette.primary.main}20 0%, transparent 50%),
                   radial-gradient(circle at 80% 20%, ${theme.palette.secondary.main}20 0%, transparent 50%)`,
      zIndex: 0,
    },

    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
    },

    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1),
    },
  },

  container: {
    position: "relative",
    zIndex: 1,
    width: "100%",
    maxWidth: "420px",
    margin: "0 auto",

    [theme.breakpoints.down("md")]: {
      maxWidth: "380px",
    },

    [theme.breakpoints.down("sm")]: {
      maxWidth: "350px",
    },

    [theme.breakpoints.down("xs")]: {
      maxWidth: "320px",
    },
  },

  resetContainer: {
    background: theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.05)"
      : "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(20px)",
    borderRadius: "24px",
    border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(255, 255, 255, 0.3)"}`,
    boxShadow: theme.palette.mode === "dark"
      ? "0 20px 40px rgba(0, 0, 0, 0.3), 0 8px 16px rgba(0, 0, 0, 0.15)"
      : "0 20px 40px rgba(0, 0, 0, 0.1), 0 8px 16px rgba(0, 0, 0, 0.05)",
    padding: theme.spacing(5, 6),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    animation: "fadeInScale 0.8s ease-out",
    maxHeight: "90vh",
    overflow: "auto",

    [theme.breakpoints.down("md")]: {
      borderRadius: "20px",
      padding: theme.spacing(4, 5),
      gap: theme.spacing(2.5),
      maxHeight: "92vh",
    },

    [theme.breakpoints.down("sm")]: {
      borderRadius: "18px",
      padding: theme.spacing(3.5, 4),
      gap: theme.spacing(2),
      maxHeight: "95vh",
    },

    [theme.breakpoints.down("xs")]: {
      borderRadius: "16px",
      padding: theme.spacing(3, 3.5),
      gap: theme.spacing(1.5),
    },
  },

  logoContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: theme.spacing(2),
    animation: "slideInUp 0.8s ease-out 0.1s both",

    "& img": {
      maxWidth: "190px",
      height: "auto",
      filter: theme.palette.mode === "dark" ? "brightness(1.1)" : "none",
    },

    [theme.breakpoints.down("md")]: {
      marginBottom: theme.spacing(1.5),
      
      "& img": {
        maxWidth: "170px",
      },
    },

    [theme.breakpoints.down("sm")]: {
      marginBottom: theme.spacing(1),
      
      "& img": {
        maxWidth: "150px",
      },
    },

    [theme.breakpoints.down("xs")]: {
      marginBottom: theme.spacing(0.5),
      
      "& img": {
        maxWidth: "130px",
      },
    },
  },

  headerText: {
    textAlign: "center",
    animation: "slideInUp 0.8s ease-out 0.2s both",
    
    "& h1": {
      fontSize: "36px",
      fontWeight: 700,
      background: theme.palette.mode === "dark"
        ? "linear-gradient(135deg, #ffffff 0%, #e0e0e0 100%)"
        : "linear-gradient(135deg, #1a1a1a 0%, #4a4a4a 100%)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
      marginBottom: theme.spacing(1),
      lineHeight: 1.2,
    },

    "& .MuiTypography-body1": {
      color: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.7)" : "rgba(71, 85, 105, 0.8)",
      fontSize: "16px",
      fontWeight: 400,
    },

    [theme.breakpoints.down("md")]: {
      "& h1": {
        fontSize: "32px",
      },
      "& .MuiTypography-body1": {
        fontSize: "15px",
      },
    },

    [theme.breakpoints.down("sm")]: {
      "& h1": {
        fontSize: "28px",
      },
      "& .MuiTypography-body1": {
        fontSize: "14px",
      },
    },

    [theme.breakpoints.down("xs")]: {
      "& h1": {
        fontSize: "24px",
      },
      "& .MuiTypography-body1": {
        fontSize: "13px",
      },
    },
  },

  inputWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2.5),
    animation: "slideInUp 0.8s ease-out 0.3s both",

    [theme.breakpoints.down("md")]: {
      gap: theme.spacing(2.2),
    },

    [theme.breakpoints.down("sm")]: {
      gap: theme.spacing(2),
    },

    [theme.breakpoints.down("xs")]: {
      gap: theme.spacing(1.8),
    },
  },

  inputContainer: {
    position: "relative",
    background: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(248, 250, 252, 0.8)",
    borderRadius: "18px",
    border: `2px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(148, 163, 184, 0.2)"}`,
    padding: theme.spacing(2.5, 3),
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    
    "&:focus-within": {
      borderColor: theme.palette.primary.main,
      background: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(255, 255, 255, 0.9)",
      transform: "translateY(-2px)",
      boxShadow: `0 8px 25px ${theme.palette.primary.main}20`,
    },

    "& .MuiInput-root": {
      fontSize: "16px",
      fontWeight: 500,
      color: theme.palette.mode === "dark" ? "#ffffff" : "#1e293b",
    },

    "& .MuiInput-input::placeholder": {
      color: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.5)" : "rgba(100, 116, 139, 0.7)",
      opacity: 1,
    },

    [theme.breakpoints.down("md")]: {
      borderRadius: "16px",
      padding: theme.spacing(2.2, 2.8),
      
      "& .MuiInput-root": {
        fontSize: "15px",
      },
    },

    [theme.breakpoints.down("sm")]: {
      borderRadius: "14px",
      padding: theme.spacing(2, 2.5),
      
      "& .MuiInput-root": {
        fontSize: "14px",
      },
    },

    [theme.breakpoints.down("xs")]: {
      borderRadius: "12px",
      padding: theme.spacing(1.8, 2.2),
      
      "& .MuiInput-root": {
        fontSize: "14px",
      },
    },
  },

  svgContainer: {
    position: "absolute",
    right: theme.spacing(3),
    top: "50%",
    transform: "translateY(-50%)",
    cursor: "pointer",
    padding: theme.spacing(0.5),
    borderRadius: "8px",
    transition: "all 0.3s ease",
    color: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.6)" : "rgba(100, 116, 139, 0.7)",

    "&:hover": {
      background: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(148, 163, 184, 0.1)",
      color: theme.palette.primary.main,
    },

    [theme.breakpoints.down("md")]: {
      right: theme.spacing(2.5),
      padding: theme.spacing(0.4),
    },

    [theme.breakpoints.down("sm")]: {
      right: theme.spacing(2.2),
      padding: theme.spacing(0.3),
    },

    [theme.breakpoints.down("xs")]: {
      right: theme.spacing(2),
    },
  },

  resetButton: {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: "#ffffff",
    borderRadius: "18px",
    padding: theme.spacing(2.5, 3),
    fontSize: "16px",
    fontWeight: 600,
    textTransform: "none",
    boxShadow: `0 8px 20px ${theme.palette.primary.main}30`,
    border: "none",
    cursor: "pointer",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    marginTop: theme.spacing(1),
    animation: "slideInUp 0.8s ease-out 0.4s both",

    "&:hover": {
      transform: "translateY(-3px)",
      boxShadow: `0 12px 28px ${theme.palette.primary.main}40`,
      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
    },

    "&:active": {
      transform: "translateY(-1px)",
    },

    "&:disabled": {
      background: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(148, 163, 184, 0.25)",
      color: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.25)" : "rgba(148, 163, 184, 0.5)",
      transform: "none",
      boxShadow: "none",
    },

    [theme.breakpoints.down("md")]: {
      borderRadius: "16px",
      padding: theme.spacing(2.2, 2.8),
      fontSize: "15px",
    },

    [theme.breakpoints.down("sm")]: {
      borderRadius: "14px",
      padding: theme.spacing(2, 2.5),
      fontSize: "14px",
    },

    [theme.breakpoints.down("xs")]: {
      borderRadius: "12px",
      padding: theme.spacing(1.8, 2.2),
      fontSize: "13px",
    },
  },

  bottomText: {
    display: "flex",
    gap: theme.spacing(1.5),
    color: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.6)" : "rgba(71, 85, 105, 0.7)",
    fontSize: "15px",
    textAlign: "center",
    justifyContent: "center",
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(148, 163, 184, 0.15)"}`,
    animation: "slideInUp 0.8s ease-out 0.5s both",
    
    "& a": {
      textDecoration: "none",
      color: theme.palette.primary.main,
      fontWeight: 600,
      transition: "all 0.3s ease",
      position: "relative",
      
      "&::after": {
        content: '""',
        position: "absolute",
        bottom: "-2px",
        left: 0,
        width: "0",
        height: "2px",
        background: theme.palette.primary.main,
        transition: "width 0.3s ease",
      },
      
      "&:hover": {
        color: theme.palette.primary.dark,
        transform: "translateY(-1px)",
        
        "&::after": {
          width: "100%",
        },
      },
    },

    [theme.breakpoints.down("md")]: {
      gap: theme.spacing(1.2),
      fontSize: "14px",
      marginTop: theme.spacing(1.5),
    },

    [theme.breakpoints.down("sm")]: {
      gap: theme.spacing(1),
      fontSize: "13px",
      marginTop: theme.spacing(1.2),
      flexDirection: "column",
    },

    [theme.breakpoints.down("xs")]: {
      fontSize: "12px",
      marginTop: theme.spacing(1),
    },
  },

  error: {
    color: "#ef4444",
    fontSize: "14px",
    marginTop: theme.spacing(0.5),
    animation: "slideInUp 0.3s ease-out",

    [theme.breakpoints.down("sm")]: {
      fontSize: "13px",
    },

    [theme.breakpoints.down("xs")]: {
      fontSize: "12px",
    },
  },

  success: {
    textAlign: "center",
    color: theme.palette.success.main,
    animation: "slideInUp 0.8s ease-out",

    "& h2": {
      fontSize: "24px",
      fontWeight: 600,
      marginBottom: theme.spacing(1),
    },

    "& p": {
      fontSize: "16px",
      opacity: 0.8,
    },

    [theme.breakpoints.down("sm")]: {
      "& h2": {
        fontSize: "20px",
      },
      "& p": {
        fontSize: "14px",
      },
    },
  },
});

const PasswordReset = ({ classes, theme }) => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const validatePasswords = () => {
    if (!newPassword || !confirmPassword) {
      setError("Both password fields are required");
      return false;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return false;
    }
    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters long");
      return false;
    }
    return true;
  };

  const sendPasswordReset = useCallback(async () => {
    setError("");
    
    if (!validatePasswords()) {
      return;
    }

    setIsLoading(true);

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      
      if (!token) {
        setError("Invalid reset link");
        setIsLoading(false);
        return;
      }

      const resetData = {
        token,
        password: newPassword,
      };

      const res = await axios.post("/api/users/reset-password", resetData);
      
      if (res.data.success) {
        setSuccess(true);
        setTimeout(() => {
          history.push("/auth/login");
        }, 3000);
      }
    } catch (err) {
      console.error("Password reset error:", err);
      setError(err.response?.data?.message || "An error occurred while resetting password");
    } finally {
      setIsLoading(false);
    }
  }, [newPassword, confirmPassword, history]);

  if (success) {
    return (
      <Fade in timeout={800}>
        <div className={classes.wrapper}>
          <div className={classes.container}>
            <Slide direction="up" in timeout={600}>
              <div className={classes.resetContainer}>
                <div className={classes.logoContainer}>
                  <Link href="/">
                    <img
                      src={
                        theme.palette.mode === "light"
                          ? "/images/Auralyze_ai_black_logo.png"
                          : "/images/auralyze_logo_white.png"
                      }
                      alt="Auralyze Logo"
                    />
                  </Link>
                </div>
                
                <div className={classes.success}>
                  <Typography variant="h2" component="h2">
                    Password Reset Successful!
                  </Typography>
                  <Typography variant="body1">
                    Your password has been successfully reset. You will be redirected to the login page shortly.
                  </Typography>
                </div>
              </div>
            </Slide>
          </div>
        </div>
      </Fade>
    );
  }

  return (
    <Fade in timeout={800}>
      <div className={classes.wrapper}>
        <div className={classes.container}>
          <Slide direction="up" in timeout={600}>
            <div className={classes.resetContainer}>
              <div className={classes.logoContainer}>
                <Link href="/">
                  <img
                    src={
                      theme.palette.mode === "light"
                        ? "/images/Auralyze_ai_black_logo.png"
                        : "/images/auralyze_logo_white.png"
                    }
                    alt="Auralyze Logo"
                  />
                </Link>
              </div>
              
              <div className={classes.headerText}>
                <Typography variant="h1" component="h1">
                  Reset Password
                </Typography>
                <Typography variant="body1">
                  Enter your new password below
                </Typography>
              </div>
              
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  sendPasswordReset();
                }}
              >
                <div className={classes.inputWrapper}>
                  <div className={classes.inputContainer}>
                    <Input
                      disableUnderline
                      fullWidth
                      type={showPassword ? "text" : "password"}
                      placeholder="New Password"
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        if (error) setError("");
                      }}
                    />
                    <div
                      className={classes.svgContainer}
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <svg 
                          width="20" 
                          height="16" 
                          viewBox="0 0 30 20" 
                          fill="none" 
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M14.9982 15.4013C16.4982 15.4013 17.7732 14.8763 18.8232 13.8263C19.8732 12.7763 20.3982 11.5013 20.3982 10.0013C20.3982 8.5013 19.8732 7.2263 18.8232 6.1763C17.7732 5.1263 16.4982 4.6013 14.9982 4.6013C13.4982 4.6013 12.2232 5.1263 11.1732 6.1763C10.1232 7.2263 9.59818 8.5013 9.59818 10.0013C9.59818 11.5013 10.1232 12.7763 11.1732 13.8263C12.2232 14.8763 13.4982 15.4013 14.9982 15.4013Z" fill="currentColor"/>
                        </svg>
                      ) : (
                        <svg 
                          width="20" 
                          height="16" 
                          viewBox="0 0 30 20" 
                          fill="none" 
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M15 19.3346C12.0833 19.3346 9.48959 18.518 7.21875 16.8846C4.94792 15.2513 3.16667 13.0569 1.875 10.3013L1.66667 9.98464L1.875 9.66797C3.16667 6.91797 4.94792 4.72797 7.21875 3.09797C9.48959 1.46797 12.0833 0.651302 15 0.651302C17.9167 0.651302 20.5104 1.46797 22.7812 3.09797C25.0521 4.72797 26.8333 6.91797 28.125 9.66797L28.3333 9.98464L28.125 10.3013C26.8333 13.0569 25.0521 15.2513 22.7812 16.8846C20.5104 18.518 17.9167 19.3346 15 19.3346Z" fill="currentColor"/>
                          <path d="M3.33333 18.3346L26.6667 1.66797" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      )}
                    </div>
                  </div>

                  <div className={classes.inputContainer}>
                    <Input
                      disableUnderline
                      fullWidth
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm Password"
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        if (error) setError("");
                      }}
                    />
                    <div
                      className={classes.svgContainer}
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <svg 
                          width="20" 
                          height="16" 
                          viewBox="0 0 30 20" 
                          fill="none" 
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M14.9982 15.4013C16.4982 15.4013 17.7732 14.8763 18.8232 13.8263C19.8732 12.7763 20.3982 11.5013 20.3982 10.0013C20.3982 8.5013 19.8732 7.2263 18.8232 6.1763C17.7732 5.1263 16.4982 4.6013 14.9982 4.6013C13.4982 4.6013 12.2232 5.1263 11.1732 6.1763C10.1232 7.2263 9.59818 8.5013 9.59818 10.0013C9.59818 11.5013 10.1232 12.7763 11.1732 13.8263C12.2232 14.8763 13.4982 15.4013 14.9982 15.4013Z" fill="currentColor"/>
                        </svg>
                      ) : (
                        <svg 
                          width="20" 
                          height="16" 
                          viewBox="0 0 30 20" 
                          fill="none" 
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M15 19.3346C12.0833 19.3346 9.48959 18.518 7.21875 16.8846C4.94792 15.2513 3.16667 13.0569 1.875 10.3013L1.66667 9.98464L1.875 9.66797C3.16667 6.91797 4.94792 4.72797 7.21875 3.09797C9.48959 1.46797 12.0833 0.651302 15 0.651302C17.9167 0.651302 20.5104 1.46797 22.7812 3.09797C25.0521 4.72797 26.8333 6.91797 28.125 9.66797L28.3333 9.98464L28.125 10.3013C26.8333 13.0569 25.0521 15.2513 22.7812 16.8846C20.5104 18.518 17.9167 19.3346 15 19.3346Z" fill="currentColor"/>
                          <path d="M3.33333 18.3346L26.6667 1.66797" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      )}
                    </div>
                  </div>
                </div>

                {error && (
                  <Typography className={classes.error}>
                    {error}
                  </Typography>
                )}
                
                <button
                  type="submit"
                  className={classes.resetButton}
                  disabled={isLoading}
                >
                  {isLoading ? "Resetting Password..." : "Reset Password"}
                </button>
              </form>
              
              <div className={classes.bottomText}>
                <span>Remember your password?</span>
                <Link href="/auth/login">
                  Sign In
                </Link>
              </div>
            </div>
          </Slide>
        </div>
      </div>
    </Fade>
  );
};

export default withStyles(styles, { withTheme: true })(PasswordReset);
